/**
 * One deliverable (or milk row) as the stop summary prints it. Pure.
 *
 * Container words come from `print-packages.ts`, the one place that reads the
 * bulk packing plans (PROD-1500): a row with a plan prints its containers
 * ("15x Standard Hotel Pans w/ 20"), a row without one prints the vessel and
 * its servings only (D23: never a wrong count). Each row prints its portion,
 * as the Oct 21 mockup's stop sheet draws it (it supersedes Abner's Oct 19
 * "Portion sizes should NOT be in the handoff sheet"): the recipe's serving size
 * with its note, as the Production Totals print it (`portionDetail`).
 */

import { formatFractionalValue } from '@ordo/constants/measure-format'
import { normalizeMeasureUnit } from '@ordo/constants/measures'

import { milkPackageLines, type PrintPackageLine, summaryPackageLines } from '@/lib/chefs/print-packages'
import { deliverableTags } from '@/lib/chefs/print-words'
import type { CampusMilkLine, Deliverable, Temperature } from '@/lib/chefs/types'

import type { ContainerLine, StopRowData } from './units'

/** Diet tags in Matt's boxed P5 style: "HALAL", "GF/DF", "VEGAN". Halal prints once the kitchen wire carries it. */
export function dietTags(diets: readonly string[]): string[] {
  const has = new Set(diets)
  const tags: string[] = []
  if (has.has('halal')) {
    tags.push('HALAL')
  }
  if (has.has('gluten-free') && has.has('dairy-free')) {
    tags.push('GF/DF')
  } else if (has.has('gluten-free')) {
    tags.push('GF')
  } else if (has.has('dairy-free')) {
    tags.push('DF')
  }
  if (has.has('vegan')) {
    tags.push('VEGAN')
  } else if (has.has('vegetarian')) {
    tags.push('VEG')
  }
  if (has.has('pork-free')) {
    tags.push('NO PORK')
  }
  if (has.has('nut-free')) {
    tags.push('NUT FREE')
  }
  return tags
}

/**
 * The kitchen's temperature for a row (the Oct 21 canvas): how it leaves the kitchen. A menu item marked deliver cold
 * is CHILLED here even when the school serves it hot.
 */
export function deliverTemperature(d: Pick<Deliverable, 'temperature' | 'deliverCold'>): Temperature {
  return d.deliverCold ? 'chilled' : d.temperature
}

/** The school's temperature for a row: how it is served. */
export function serveTemperature(d: Pick<Deliverable, 'temperature'>): Temperature {
  return d.temperature
}

/** "fl oz", "1/2 cup" → comparable text: lowercase, single spaces, no trailing "s" or period. */
function servingTextKey(text: string): string {
  return normalizeMeasureUnit(text).replace(/s$/, '')
}

/**
 * A serving size as the Menus recipe page prints it (Sheila, Oct 23: "SERVING SIZE (NOTE)"): the amount and its unit,
 * then the serving's own name in parentheses, "1 each (Package)", "4 Wedges (1/2 Cup)"; the amount alone when the
 * serving has no name or one that only repeats the amount ("1 Calzone" named "Calzone").
 */
export function servingSizeText(portion: { quantity: number; unit: string; name?: string | null }): string | null {
  if (!Number.isFinite(portion.quantity) || portion.quantity <= 0) {
    return null
  }
  const amount = `${formatFractionalValue(portion.quantity)} ${portion.unit.trim()}`.trim()
  const name = portion.name?.trim()
  if (!name || [servingTextKey(amount), servingTextKey(portion.unit)].includes(servingTextKey(name))) {
    return amount
  }
  return `${amount} (${name})`
}

/**
 * A row's portion: each food's serving size with its note (`servingSizeText`), as the recipe states it. A food
 * listed twice in one deliverable prints its serving size ONCE, never added up: the twin entries are the same
 * portion (Sheila, Oct 23 V4: Ross Valley printed "2 Calzone", "1 Cup" melon and "2 Bagel" for the same servings
 * every other stop prints as 1). A combo's different foods are joined ("1 1/2 Cup + 1/2 Cup"). A food with no
 * recipe serving prints its own portion label, else the shipped serving. Condiments and the foods' names never
 * print here.
 */
export function portionDetail(d: Deliverable): string | null {
  const foods = new Map<string, { quantity: number; unit: string; name?: string }>()
  const labels: string[] = []
  for (const c of d.components.filter((component) => !component.isCondiment)) {
    if (!c.portion) {
      if (c.portionLabel?.trim()) {
        labels.push(c.portionLabel.trim())
      }
      continue
    }
    const key = `${c.id}|${normalizeMeasureUnit(c.portion.unit)}`
    if (!foods.has(key)) {
      foods.set(key, c.portion)
    }
  }
  const parts = [...[...foods.values()].map(servingSizeText), ...labels].filter((part): part is string => Boolean(part))
  return parts.length > 0 ? [...new Set(parts)].join(' + ') : d.servingSize?.trim() || null
}

export function containerLines(lines: readonly PrintPackageLine[]): ContainerLine[] {
  return lines.map((line) => ({
    count: line.count,
    name: line.name,
    fill: line.fill,
    ...(line.section ? { section: true as const } : {}),
  }))
}

/**
 * A stop summary line (Abner, Oct 18: the pack list and the stop sheet in one
 * table): "Applesauce | 325 Servings | 15x Standard Hotel Pans w/ 20, 1x … w/
 * 13 | HOT | °F | °F | ☐". The name stands alone, as drawn. A counted item
 * with no package ships "Loose".
 */
export function stopRow(d: Deliverable): StopRowData {
  const lines = containerLines(summaryPackageLines(d))
  return {
    name: d.name,
    diets: dietTags(d.diets),
    tags: deliverableTags(d),
    portion: portionDetail(d),
    total: d.qty,
    unit: 'Servings',
    containers: lines.length > 0 ? lines : [{ count: null, name: 'Loose', fill: null }],
    temperature: deliverTemperature(d),
    serveTemperature: serveTemperature(d),
    heatOnSite: d.deliverCold === true && d.temperature === 'hot',
    noTemps: deliverTemperature(d) === 'ambient',
  }
}

/** A line of the stop summary's milk table: the whole row, with the containers its plan packs or the package it names. */
export function milkStopRow(line: CampusMilkLine): StopRowData {
  return {
    name: line.name,
    diets: dietTags(line.component.diets),
    tags: [],
    portion: (line.component.portion ? servingSizeText(line.component.portion) : null) ?? line.servingSize ?? null,
    total: line.qty,
    unit: 'Servings',
    // Only a package the milk names as its own (`milkOuterPackaging`): never "Loose" or the card's pan.
    containers: containerLines(milkPackageLines(line)),
    temperature: line.temperature,
    serveTemperature: line.temperature,
    heatOnSite: false,
    noTemps: line.temperature === 'ambient',
  }
}
